import { supabase } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import { createContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  getRefreshToken,
  setRefreshToken,
  removeRefreshToken
} from '@/lib/cookies';
import { checkCookieFormat } from '@/lib/cookie-format';

export interface AuthContextType {
  session: Session | null;
  isLoading: boolean;
  checkCookieAuth: () => Promise<boolean>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// สร้าง provider component
function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Ref to prevent concurrent checkCookieAuth calls (race condition prevention)
  const isVerifyingRef = useRef(false);
  // Cache the result of a recent successful verification to prevent redundant calls
  const lastVerificationRef = useRef<{ result: boolean; timestamp: number } | null>(null);

  /**
   * Check for authentication token in cookies and verify it
   * @returns True if a valid cookie auth token was found
   */
  const checkCookieAuth = async (): Promise<boolean> => {
    // Prevent concurrent verification calls (race condition prevention)
    if (isVerifyingRef.current) {
      // If we have a recent cached result, return it
      if (lastVerificationRef.current && Date.now() - lastVerificationRef.current.timestamp < 5000) {
        return lastVerificationRef.current.result;
      }
      return false;
    }

    isVerifyingRef.current = true;

    try {
      // Check for auth token in cookies
      const token = getAuthToken();
      const refreshToken = getRefreshToken();

      if (!token) {
        lastVerificationRef.current = { result: false, timestamp: Date.now() };
        return false;
      }

      // Try to verify token directly
      const { data: userData, error: userError } = await supabase.auth.getUser(token);

      if (!userError && userData.user) {
        // Token is still valid
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          setSession(sessionData.session);
          setIsLoading(false);
          lastVerificationRef.current = { result: true, timestamp: Date.now() };
          return true;
        }
      }

      // If we have a refresh token, try to refresh the session
      if (refreshToken) {
        const { data, error } = await supabase.auth.refreshSession({
          refresh_token: refreshToken,
        });

        if (error || !data.session) {
          console.error('Failed to refresh session with cookie token:', error);
          removeAuthToken();
          removeRefreshToken();
          lastVerificationRef.current = { result: false, timestamp: Date.now() };
          return false;
        }

        // Update tokens in cookies
        setAuthToken(data.session.access_token);
        if (data.session.refresh_token) {
          setRefreshToken(data.session.refresh_token);
        }

        // Update session state
        setSession(data.session);
        setIsLoading(false);

        lastVerificationRef.current = { result: true, timestamp: Date.now() };
        return true;
      } else {
        // No refresh token available
        console.error('Cookie auth token verification failed and no refresh token available');
        removeAuthToken();
        lastVerificationRef.current = { result: false, timestamp: Date.now() };
        return false;
      }
    } catch (error) {
      console.error('Error verifying cookie auth token:', error);
      removeAuthToken();
      removeRefreshToken();
      lastVerificationRef.current = { result: false, timestamp: Date.now() };
      return false;
    } finally {
      isVerifyingRef.current = false;
    }
  };

  /**
   * Sign out the user and clear the auth token cookie
   */
  const signOut = async (): Promise<void> => {
    await supabase.auth.signOut();
    removeAuthToken();
    removeRefreshToken();
    setSession(null);
  };

  useEffect(() => {
    // Temporary: Check cookie format to help debug
    checkCookieFormat();
    
    async function initAuth() {
      // First try to get session from Supabase
      const { data: { session: supabaseSession } } = await supabase.auth.getSession();
      
      if (supabaseSession) {
        console.log('Got supabase session:', supabaseSession);
        // If we have a session, store the tokens in cookies for universal login
        setAuthToken(supabaseSession.access_token);
        if (supabaseSession.refresh_token) {
          setRefreshToken(supabaseSession.refresh_token);
        }
        
        // Check cookies right after setting
        setTimeout(() => {
          checkCookieFormat();
        }, 100);
        
        setSession(supabaseSession);
        setIsLoading(false);
      } else {
        // If no session from Supabase, check cookies for a token
        const cookieAuthSuccess = await checkCookieAuth();
        if (!cookieAuthSuccess) {
          // If no cookie token or verification failed
          setIsLoading(false);
        }
      }
    }
    
    initAuth();

    // Subscribe to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change event:', event);
      
      if (event === 'SIGNED_IN' && session) {
        // When user signs in, store tokens in cookies using centralized utilities
        // (cookies.ts handles the correct domain and format)
        setAuthToken(session.access_token);
        if (session.refresh_token) {
          setRefreshToken(session.refresh_token);
        }
      } else if (event === 'SIGNED_OUT') {
        // When user signs out, remove tokens from cookies
        removeAuthToken();
        removeRefreshToken();
      } else if (event === 'TOKEN_REFRESHED' && session) {
        // When token is refreshed, update cookies
        setAuthToken(session.access_token);
        if (session.refresh_token) {
          setRefreshToken(session.refresh_token);
        }
      }
      
      setSession(session);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  console.log('session', session);

  const value = {
    session,
    isLoading,
    checkCookieAuth,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthProvider };
